import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import addEventListener from '../_util/addEventListener';
import Affix from '../affix';
import scrollTo from '../_util/scrollTo';
import getScroll from '../_util/getScroll';
import ConfigContext from '../config-provider/ConfigContext';
import { AnchorContextProvider } from './AnchorContext';
function getDefaultContainer() {
    return window;
}
function getOffsetTop(element, container) {
    if (!element) {
        return 0;
    }
    if (!element.getClientRects().length) {
        return 0;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width || rect.height) {
        if (container === window && element.ownerDocument) {
            container = element.ownerDocument.documentElement;
            return rect.top - container.clientTop;
        }
        return rect.top - container.getBoundingClientRect().top;
    }
    return rect.top;
}
const sharpMatcherRegx = /#([^#]+)$/;
export default class Anchor extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            activeLink: null,
        };
        this.links = [];
        this.handleScrollTo = (link) => {
            const { offsetTop, getContainer, targetOffset } = this.props;
            this.setState({ activeLink: link });
            const container = getContainer();
            const scrollTop = getScroll(container, true);
            const sharpLinkMatch = sharpMatcherRegx.exec(link);
            if (!sharpLinkMatch) {
                return;
            }
            const targetElement = document.getElementById(sharpLinkMatch[1]);
            if (!targetElement) {
                return;
            }
            const eleOffsetTop = getOffsetTop(targetElement, container);
            let y = scrollTop + eleOffsetTop;
            y -= targetOffset !== undefined ? targetOffset : offsetTop || 0;
            this.animating = true;
            scrollTo(y, {
                callback: () => {
                    this.animating = false;
                },
                getContainer,
            });
        };
        this.saveInkNode = (node) => {
            this.inkNode = node;
        };
        this.handleScroll = () => {
            if (this.animating) {
                return;
            }
            const { activeLink } = this.state;
            const { offsetTop, bounds, targetOffset } = this.props;
            const currentActiveLink = this.getCurrentAnchor(targetOffset !== undefined ? targetOffset : offsetTop || 0, bounds);
            if (activeLink !== currentActiveLink) {
                this.setState({
                    activeLink: currentActiveLink,
                });
            }
        };
        this.updateInk = () => {
            if (typeof document === 'undefined') {
                return;
            }
            const prefixCls = this.getPrefixCls();
            const linkNode = findDOMNode(this).getElementsByClassName(`${prefixCls}-link-title-active`)[0];
            if (linkNode && linkNode.offsetParent) {
                this.inkNode.style.top = `${linkNode.offsetTop + linkNode.clientHeight / 2 - 4.5}px`;
            }
        };
    }
    static get contextType() {
        return ConfigContext;
    }
    getContextValue() {
        const { onClick } = this.props;
        const { activeLink } = this.state;
        const c7nAnchor = {
            registerLink: (link) => {
                if (!this.links.includes(link)) {
                    this.links.push(link);
                }
            },
            unregisterLink: (link) => {
                const index = this.links.indexOf(link);
                if (index !== -1) {
                    this.links.splice(index, 1);
                }
            },
            activeLink,
            scrollTo: this.handleScrollTo,
            onClick,
        };
        return { c7nAnchor };
    }
    componentDidMount() {
        const { getContainer } = this.props;
        this.scrollContainer = getContainer();
        this.scrollEvent = addEventListener(this.scrollContainer, 'scroll', this.handleScroll);
        this.handleScroll();
    }
    componentDidUpdate() {
        if (this.scrollEvent) {
            const { getContainer } = this.props;
            const currentContainer = getContainer();
            if (this.scrollContainer !== currentContainer) {
                this.scrollContainer = currentContainer;
                this.scrollEvent.remove();
                this.scrollEvent = addEventListener(this.scrollContainer, 'scroll', this.handleScroll);
                this.handleScroll();
            }
        }
        this.updateInk();
    }
    componentWillUnmount() {
        if (this.scrollEvent) {
            this.scrollEvent.remove();
        }
    }
    getCurrentAnchor(offsetTop = 0, bounds = 5) {
        const { getCurrentAnchor } = this.props;
        if (typeof getCurrentAnchor === 'function') {
            return getCurrentAnchor();
        }
        const activeLink = '';
        if (typeof document === 'undefined') {
            return activeLink;
        }
        const linkSections = [];
        const { getContainer } = this.props;
        const container = getContainer();
        this.links.forEach(link => {
            const sharpLinkMatch = sharpMatcherRegx.exec(link.toString());
            if (!sharpLinkMatch) {
                return;
            }
            const target = document.getElementById(sharpLinkMatch[1]);
            if (target) {
                const top = getOffsetTop(target, container);
                if (top < offsetTop + bounds) {
                    linkSections.push({
                        link,
                        top,
                    });
                }
            }
        });
        if (linkSections.length) {
            const maxSection = linkSections.reduce((prev, curr) => (curr.top > prev.top ? curr : prev));
            return maxSection.link;
        }
        return '';
    }
    getPrefixCls() {
        const { prefixCls } = this.props;
        const { getPrefixCls } = this.context;
        return getPrefixCls('anchor', prefixCls);
    }
    render() {
        const { className = '', style, offsetTop, affix, showInkInFixed, children, getContainer, } = this.props;
        const { activeLink } = this.state;
        const { getPrefixCls } = this.context;
        const prefixCls = this.getPrefixCls();
        const inkClass = classNames(`${prefixCls}-ink-ball`, {
            visible: activeLink,
        });
        const wrapperClass = classNames(className, `${prefixCls}-wrapper`);
        const anchorClass = classNames(prefixCls, {
            fixed: !affix && !showInkInFixed,
        });
        const wrapperStyle = {
            maxHeight: offsetTop ? `calc(100vh - ${offsetTop}px)` : '100vh',
            ...style,
        };
        const anchorContent = (React.createElement("div", { className: wrapperClass, style: wrapperStyle },
            React.createElement("div", { className: anchorClass },
                React.createElement("div", { className: `${prefixCls}-ink` },
                    React.createElement("span", { className: inkClass, ref: this.saveInkNode })),
                React.createElement(AnchorContextProvider, Object.assign({}, this.getContextValue(), { getPrefixCls: getPrefixCls }), children))));
        return !affix ? (anchorContent) : (React.createElement(Affix, { offsetTop: offsetTop, target: getContainer }, anchorContent));
    }
}
Anchor.displayName = 'Anchor';
Anchor.defaultProps = {
    affix: true,
    showInkInFixed: false,
    getContainer: getDefaultContainer,
};
